// ==========================================
// src/pages/TipoCargaSEO.jsx
// PÁGINA DE TIPO DE CARGA PARA SEO — APARECE NO GOOGLE
// Ex: /tipo-carga/soja, /carga-soja
// ==========================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  Truck, ArrowRight, Loader2, Search,
  Package, Weight, DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const tiposMap = {
  soja: 'Soja',
  milho: 'Milho',
  farelo: 'Farelo de Soja',
  acucar: 'Açúcar',
  algodao: 'Algodão',
  arroz: 'Arroz',
  carne: 'Carne',
  frango: 'Frango',
  suino: 'Suíno',
  granel_solido: 'Granel Sólido',
  granel_liquido: 'Granel Líquido',
  refrigerada: 'Refrigerada',
  perigosa: 'Perigosa',
  container: 'Container',
  carga_geral: 'Carga Geral'
};

export default function TipoCargaSEO() {
  const { tipoSlug } = useParams();
  const navigate = useNavigate();
  const [cargas, setCargas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipoCarga, setTipoCarga] = useState('');

  useEffect(() => {
    const carregarCargas = async () => {
      try {
        setLoading(true);
        
        // Converte slug para tipo_carga do banco
        // Ex: "soja" → "soja", "granel-solido" → "granel_solido"
        const tipoFormatado = tipoSlug?.replace(/-/g, '_') || '';
        setTipoCarga(tipoFormatado);

        const { data, error } = await supabase
          .from('cargas')
          .select('*')
          .eq('status', 'disponivel')
          .eq('tipo_carga', tipoFormatado)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setCargas(data || []);
      } catch (err) {
        console.error('Erro:', err);
      } finally {
        setLoading(false);
      }
    };

    carregarCargas();
  }, [tipoSlug]);

  const formatarValor = (valor) => {
    if (!valor) return 'A combinar';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const tipoLabel = tiposMap[tipoCarga] || tipoCarga;

  // ✅ META TAGS DINÂMICAS
  useEffect(() => {
    if (tipoCarga) {
      const title = `Frete de ${tipoLabel} | Cargas Disponíveis | BoraFrete`;
      const description = `Encontre fretes de ${tipoLabel} para transporte. ${cargas.length} cargas disponíveis. Cadastre-se grátis no BoraFrete!`;
      
      document.title = title;
      
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', description);
    }
  }, [tipoCarga, tipoLabel, cargas.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* HEADER SEO */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Fretes de {tipoLabel}
          </h1>
          <p className="text-gray-600">
            {cargas.length} {cargas.length === 1 ? 'carga disponível' : 'cargas disponíveis'} para transporte de {tipoLabel}
          </p>
        </div>

        {/* LISTA DE CARGAS */}
        {cargas.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Nenhuma carga de {tipoLabel} disponível no momento</p>
            <Button onClick={() => navigate('/buscar-cargas')} className="mt-4">
              Ver outros tipos de carga
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {cargas.map((carga) => (
              <div
                key={carga.id}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => navigate(`/frete/${carga.slug || carga.id}`)}
              >
                <div className="flex items-center justify-between mb-3">
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                    Disponível
                  </Badge>
                  <span className="text-sm font-bold text-blue-600">
                    {formatarValor(carga.valor_total || carga.valor_frete)}
                  </span>
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1">
                    <p className="font-bold text-gray-900">
                      {carga.origem_cidade} - {carga.origem_estado}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                  <div className="flex-1">
                    <p className="font-bold text-gray-900">
                      {carga.destino_cidade} - {carga.destino_estado}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Package className="w-4 h-4" />
                    {carga.tipo_carga || 'Carga'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Weight className="w-4 h-4" />
                    {carga.peso_valor} {carga.peso_unidade || 'ton'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Truck className="w-4 h-4" />
                    {carga.tipo_veiculo || 'N/I'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">
            Quer encontrar mais tipos de carga?
          </p>
          <Button onClick={() => navigate('/buscar-cargas')} className="bg-blue-600 hover:bg-blue-700">
            <Search className="w-4 h-4 mr-2" />
            Buscar todas as cargas
          </Button>
        </div>
      </div>
    </div>
  );
}